import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import '../styles/LastGamesViewer.css';
import { inferMatchTypeCategory } from '../utils/matchTypeCatalog';

const normalizeText = (value) => String(value || '').trim().toUpperCase();

const normalizeMode = (value) => {
  const raw = normalizeText(value);
  if (raw === 'HARDPOINT') return 'HP';
  if (raw === 'SEARCH' || raw === 'SEARCH & DESTROY' || raw === 'SEARCH AND DESTROY') return 'SND';
  if (raw === 'CONTROL') return 'CTL';
  return raw;
};

const normalizeResult = (value) => {
  const raw = normalizeText(value);
  if (raw === 'WIN' || raw === 'W') return 'WIN';
  if (raw === 'LOSS' || raw === 'LOSE' || raw === 'L') return 'LOSS';
  return '';
};

const toDateMillis = (value) => {
  if (!value) return 0;
  if (typeof value?.toMillis === 'function') return value.toMillis();
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const toDateKey = (value) => {
  if (!value) return '';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }
  const millis = toDateMillis(value);
  if (!millis) return '';
  const date = new Date(millis);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const normalizeList = (value) => {
  const list = Array.isArray(value) ? value : [value];
  return [...new Set(list.map((item) => normalizeText(item)).filter(Boolean))];
};

const findCatalogMatchesForType = (catalog = [], matchType = '') => {
  const typeKey = normalizeText(matchType);
  if (!typeKey) return [];
  return (catalog || []).filter((item) => normalizeText(item?.label || item?.name || item?.MatchType) === typeKey);
};

const rowMatchesTeam = (row, teamName) => {
  const teamKeys = normalizeList(teamName);
  if (!teamKeys.length) return true;
  const rowKeys = normalizeList([
    row?.Team,
    row?.TeamName,
    row?.OwnerTeam,
    row?.EnemyTeam,
    row?.Opponent,
    row?.VS,
  ]);
  return rowKeys.some((key) => teamKeys.includes(key));
};

const rowMatchesSharedFilters = (row, filters = {}) => {
  const rowDate = toDateKey(row?.Date || row?.date);
  if (filters.startDate && rowDate && rowDate < filters.startDate) return false;
  if (filters.endDate && rowDate && rowDate > filters.endDate) return false;

  const rowMatchType = normalizeText(row?.MatchType || row?.matchType || row?.Type || row?.type);
  const catalogMatches = findCatalogMatchesForType(filters.matchTypeCatalog || [], rowMatchType);
  const categoryKey = normalizeText(filters.matchCategory);
  if (categoryKey) {
    const rowCategory = normalizeText(row?.MatchCategory || row?.matchCategory);
    const categoryMatches = rowCategory
      ? rowCategory === categoryKey
      : catalogMatches.length
        ? catalogMatches.some((entry) => normalizeText(entry?.category) === categoryKey)
        : inferMatchTypeCategory(rowMatchType) === categoryKey;
    if (!categoryMatches) return false;
  }

  const selectedTypeRegions = normalizeList(filters.matchTypeRegion || []);
  if (selectedTypeRegions.length) {
    const rowTypeRegion = normalizeText(row?.MatchTypeRegion || row?.matchTypeRegion);
    const catalogRegions = catalogMatches
      .filter((entry) => !categoryKey || normalizeText(entry?.category) === categoryKey)
      .map((entry) => normalizeText(entry?.region || 'GLOBAL'))
      .filter(Boolean);
    const rowTypeRegions = rowTypeRegion
      ? [rowTypeRegion]
      : catalogRegions.length
        ? catalogRegions
        : ['GLOBAL'];
    if (!rowTypeRegions.some((regionKey) => selectedTypeRegions.includes(regionKey))) return false;
  }

  const selectedMatchTypes = normalizeList(filters.matchTypes || []);
  if (selectedMatchTypes.length) {
    if (!selectedMatchTypes.includes(rowMatchType)) return false;
  }

  return true;
};

const buildRowKey = (row) => (
  row?.id ||
  row?.ModeID ||
  row?.matchGroupId ||
  `${row?.Team || ''}|${row?.EnemyTeam || ''}|${row?.Mode || ''}|${row?.Map || ''}|${row?.Date || ''}|${row?.PF || ''}|${row?.PA || ''}`
);

export default function LastGamesViewer({
  teamId,
  seasonId = null,
  altSeasonId = null,
  teamName,
  isEnemy = false,
  region = '',
  sharedFilters = {}
}) {
  const [mode, setMode] = useState('All');
  const [count, setCount] = useState(15);
  const [games, setGames] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      const TEAM = normalizeText(teamName);
      if (!teamId || !TEAM) return;

      const collectionName = isEnemy ? 'enemyStats' : 'teamStats';
      const seasonCandidates = [...new Set(
        [seasonId, altSeasonId]
          .filter(Boolean)
          .map((value) => String(value))
      )];
      const buildSeasonSegs = (seasonKey) => ['teams', teamId, 'seasons', seasonKey, 'database', collectionName];
      const legacySegs = ['teams', teamId, 'database', collectionName];

      const normalizeRows = (rows, options = {}) => rows
        .filter((r) => options.ignoreTeam ? true : rowMatchesTeam(r, TEAM))
        .filter((r) => {
          const regionKey = normalizeText(region);
          const rowRegion = normalizeText(r?.Region || r?.region);
          return !regionKey || !rowRegion || rowRegion === regionKey;
        })
        .filter((r) => (mode === 'All' ? true : normalizeMode(r.Mode) === normalizeMode(mode)))
        .filter((r) => rowMatchesSharedFilters(r, sharedFilters))
        .sort((a, b) => toDateMillis(b?.Date) - toDateMillis(a?.Date));

      const dedupeRows = (rows) => {
        const seen = new Set();
        return rows.filter((row) => {
          const key = buildRowKey(row);
          if (!key || seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      };

      const loadModeRows = async (baseSegs, options = {}) => {
        const ref = collection(db, ...baseSegs, 'modes');
        try {
          const snap = await getDocs(ref);
          const sourceRows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          const strictRows = normalizeRows(sourceRows).slice(0, count);
          if (strictRows.length || !options.ignoreTeamFallback) return strictRows;
          return normalizeRows(sourceRows, { ignoreTeam: true }).slice(0, count);
        } catch {
          const all = await getDocs(ref);
          const sourceRows = all.docs.map((d) => ({ id: d.id, ...d.data() }));
          const strictRows = normalizeRows(sourceRows).slice(0, count);
          if (strictRows.length || !options.ignoreTeamFallback) return strictRows;
          return normalizeRows(sourceRows, { ignoreTeam: true }).slice(0, count);
        }
      };

      const fetchEnemyRows = async (baseSegs) => {
        const modeRef = collection(db, ...baseSegs, 'modes');
        const matchGroupsRef = collection(db, ...baseSegs, 'matchGroups');
        const [modeSnap, matchGroupSnap] = await Promise.all([
          getDocs(modeRef),
          getDocs(matchGroupsRef),
        ]);
        const rows = [
          ...modeSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
          ...matchGroupSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
        ];
        return dedupeRows(normalizeRows(rows)).slice(0, count);
      };

      try {
        if (isEnemy) {
          let rows = [];
          for (const seasonKey of seasonCandidates) {
            rows = await fetchEnemyRows(buildSeasonSegs(seasonKey));
            if (rows.length) break;
          }
          if (!rows.length) rows = await fetchEnemyRows(legacySegs);
          if (!alive) return;
          setGames(rows);
          return;
        }

        let rows = [];
        for (const seasonKey of seasonCandidates) {
          rows = await loadModeRows(buildSeasonSegs(seasonKey), {
            ignoreTeamFallback: !isEnemy,
          });
          if (rows.length) break;
        }
        if (!rows.length) {
          rows = await loadModeRows(legacySegs, {
            ignoreTeamFallback: !isEnemy,
          });
        }
        if (!alive) return;
        setGames(rows);
      } catch (err) {
        if (!alive) return;
        console.error('Failed to load LastGames rows', err);
        setGames([]);
      }
    })();

    return () => { alive = false; };
  }, [teamId, seasonId, altSeasonId, teamName, isEnemy, region, sharedFilters, mode, count]);

  const fmtDate = (d) => {
    if (!d) return '';
    if (d.toDate) return d.toDate().toLocaleDateString();
    try { return new Date(d).toLocaleDateString(); } catch { return String(d); }
  };

  return (
    <div className="map-performance-container" style={{ marginTop: 16 }}>
      <div className="table-header table-header--center">
        <div className="table-title" style={{ margin: 0 }}>Last Games</div>
        <div className="table-header-controls">
          <label>Mode</label>
          <select className="limit-dropdown" value={mode} onChange={(e) => setMode(e.target.value)}>
            {['All', 'HP', 'SND', 'CTL'].map((m) => <option key={m} value={m}>{m}</option>)}
          </select>

          <label>Show</label>
          <select className="limit-dropdown" value={count} onChange={(e) => setCount(Number(e.target.value))}>
            {[5, 10, 15, 20].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      </div>

      <div className="table-wrapper">
        <table className="performance-table performance-table--last">
          <thead>
            <tr>
              <th>#</th><th>Date</th><th>Mode</th><th>Map</th><th>Result</th><th>Score</th><th>Team</th>
            </tr>
          </thead>
          <tbody>
            {games.length === 0 && (
              <tr><td colSpan="7" style={{ textAlign: 'center' }}>No games found</td></tr>
            )}
            {games.map((g, idx) => {
              const result = normalizeResult(g.WinLose || g.Result);
              return (
                <tr
                  key={g.id ?? idx}
                  className={result === 'WIN' ? 'win-row' : result === 'LOSS' ? 'loss-row' : ''}
                >
                  <td>{idx + 1}</td>
                  <td>{fmtDate(g.Date)}</td>
                  <td>{g.Mode}</td>
                  <td>{g.Map || '-'}</td>
                  <td>{g.WinLose || g.Result || '-'}</td>
                  <td>{`${g.PF}-${g.PA}`}</td>
                  <td>{g.EnemyTeam || g.Team || '-'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
